// google-pay.mjs - JavaScript module for Google Pay operations in Kotlin/Wasm

/**
 * JsResult class for handling success and error states in Kotlin/WASM interop
 * This avoids throwing exceptions which don't work well with WASM
 */
class JsResult {
    constructor(data = null, error = null) {
        this.data = data;
        this.error = error;
    }

    static success(data) {
        return new JsResult(data, null);
    }

    static error(errorMessage) {
        return new JsResult(null, errorMessage);
    }

    get isSuccess() {
        return this.error === null;
    }

    get isError() {
        return this.error !== null;
    }
}

/**
 * Create Google Pay payment request object
 * @param {string} merchantId - Google Pay Business Console merchant identifier (BCR2DN... format)
 * @param {string} merchantName - Merchant name
 * @param {string} acceptorId - Acceptor identifier (used as gatewayMerchantId for bluemedia)
 * @param {string} totalPrice - Total price as string
 * @param {string} gateway - Payment gateway identifier
 * @param {string} currencyCode - Currency code (default: PLN)
 * @returns {JsResult} JsResult containing Google Pay payment request object or error
 */
export function createPaymentRequest(merchantId, merchantName, acceptorId, totalPrice, gateway, currencyCode) {
    console.log("[JS] createPaymentRequest gateway:", gateway);
    console.log("[JS] createPaymentRequest called with merchantId:", merchantId, "merchantName:", merchantName, "acceptorId:", acceptorId, "totalPrice:", totalPrice, "currencyCode:", currencyCode);

    // Check for null/undefined parameters and return error if any required param is missing
    if (merchantId == null) {
        console.warn("[JS] createPaymentRequest: merchantId is null/undefined");
        return JsResult.error("merchantId is required but is null or undefined");
    }
    if (merchantName == null) {
        console.warn("[JS] createPaymentRequest: merchantName is null/undefined");
        return JsResult.error("merchantName is required but is null or undefined");
    }
    if (acceptorId == null) {
        console.warn("[JS] createPaymentRequest: acceptorId is null/undefined");
        return JsResult.error("acceptorId is required but is null or undefined");
    }
    if (totalPrice == null) {
        console.warn("[JS] createPaymentRequest: totalPrice is null/undefined");
        return JsResult.error("totalPrice is required but is null or undefined");
    }
    if (gateway == null) {
        console.warn("[JS] createPaymentRequest: gateway is null/undefined");
        return JsResult.error("gateway is required but is null or undefined");
    }

    const paymentRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [{
            type: 'CARD',
            parameters: {
                allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                allowedCardNetworks: ['MASTERCARD', 'VISA']
            },
            tokenizationSpecification: {
                type: 'PAYMENT_GATEWAY',
                parameters: {
                    'gateway': gateway,
                    'gatewayMerchantId': acceptorId
                }
            }
        }],
        merchantInfo: {
            merchantId: merchantId,
            merchantName: merchantName
        },
        transactionInfo: {
            totalPriceStatus: 'FINAL',
            totalPrice: totalPrice,
            currencyCode: currencyCode || 'PLN'
        }
    };

    console.log("[JS] createPaymentRequest: payment request object created successfully");
    console.log("[JS] createPaymentRequest: tokenizationSpecification.parameters:", JSON.stringify(paymentRequest.allowedPaymentMethods[0].tokenizationSpecification.parameters));
    return JsResult.success(paymentRequest);
}

/**
 * Load payment data from Google Pay API
 * @param {Object} paymentDataRequest - Payment data request object
 * @param {string} environment - Google Pay environment: 'TEST' or 'PRODUCTION'
 * @returns {Promise<JsResult>} Promise that resolves to JsResult containing payment data or error
 */
export async function loadPaymentData(paymentDataRequest, environment) {
    console.log("[JS] loadPaymentData called with request:", paymentDataRequest, "environment:", environment);

    // Check if paymentDataRequest is null/undefined
    if (paymentDataRequest == null) {
        console.warn("[JS] loadPaymentData: paymentDataRequest is null/undefined");
        return JsResult.error("Payment data request is null or undefined");
    }

    // Validate environment parameter
    if (environment == null || (environment !== 'TEST' && environment !== 'PRODUCTION')) {
        console.warn("[JS] loadPaymentData: invalid environment, defaulting to TEST");
        environment = 'TEST';
    }

    // Step-by-step validation of Google Pay API chain
    if (typeof google === 'undefined' || google == null) {
        console.warn("[JS] loadPaymentData: 'google' is not available or null/undefined");
        return JsResult.error("Google Pay API not loaded - 'google' object is not available");
    }
    console.log("[JS] loadPaymentData: 'google' object is available");

    if (typeof google.payments === 'undefined' || google.payments == null) {
        console.warn("[JS] loadPaymentData: 'google.payments' is not available or null/undefined");
        return JsResult.error("Google Pay API not loaded - 'google.payments' is not available");
    }
    console.log("[JS] loadPaymentData: 'google.payments' object is available");

    if (typeof google.payments.api === 'undefined' || google.payments.api == null) {
        console.warn("[JS] loadPaymentData: 'google.payments.api' is not available or null/undefined");
        return JsResult.error("Google Pay API not loaded - 'google.payments.api' is not available");
    }
    console.log("[JS] loadPaymentData: 'google.payments.api' object is available");

    if (typeof google.payments.api.PaymentsClient === 'undefined' || google.payments.api.PaymentsClient == null) {
        console.warn("[JS] loadPaymentData: 'google.payments.api.PaymentsClient' is not available or null/undefined");
        return JsResult.error("Google Pay API not loaded - 'google.payments.api.PaymentsClient' is not available");
    }
    console.log("[JS] loadPaymentData: 'google.payments.api.PaymentsClient' constructor is available");

    try {
        console.log("[JS] loadPaymentData: Creating PaymentsClient with environment:", environment);
        const paymentsClient = new google.payments.api.PaymentsClient({environment: environment});
        console.log("[JS] loadPaymentData: PaymentsClient instance created successfully");

        if (paymentsClient == null) {
            console.warn("[JS] loadPaymentData: PaymentsClient instance is null");
            return JsResult.error("Failed to create PaymentsClient instance");
        }

        if (typeof paymentsClient.loadPaymentData !== 'function') {
            console.warn("[JS] loadPaymentData: PaymentsClient.loadPaymentData method is not available");
            return JsResult.error("PaymentsClient.loadPaymentData method is not available");
        }
        console.log("[JS] loadPaymentData: calling PaymentsClient.loadPaymentData");

        const paymentDataPromise = paymentsClient.loadPaymentData(paymentDataRequest);
        console.log("[JS] loadPaymentData: PaymentsClient.loadPaymentData called successfully, awaiting promise");

        const paymentData = await paymentDataPromise;
        console.log("[JS] loadPaymentData: Promise resolved successfully");
        return JsResult.success(paymentData);

    } catch (error) {
        console.warn("[JS] loadPaymentData: Error creating PaymentsClient or calling loadPaymentData:", error);

        // Check if this is a user cancellation (Google Pay returns statusCode: 'CANCELED')
        if (error.statusCode === 'CANCELED') {
            console.log("[JS] Google Pay cancelled by user");
            console.log("[JS] User cancellation is normal - payment sheet dismissed without completing");
            // User cancellation is NOT an error - return specific error code for graceful handling
            return JsResult.error('USER_CANCELLED_GOOGLE_PAY');
        }

        // For all other errors, return a generic error message
        return JsResult.error(`Error creating PaymentsClient or calling loadPaymentData: ${error.message || error}`);
    }
}

/**
 * Extract payment token from Google Pay response
 * @param {Object} paymentData - Payment data from Google Pay
 * @returns {JsResult} JsResult containing payment token or error
 */
export function extractToken(paymentData) {
    console.log("[JS] extractToken called with paymentData:", paymentData);

    // Check if paymentData is null/undefined
    if (paymentData == null) {
        console.warn("[JS] extractToken: paymentData is null/undefined");
        return JsResult.error("Payment data is null or undefined");
    }

    // Step-by-step validation of payment data structure
    if (typeof paymentData.paymentMethodData === 'undefined' || paymentData.paymentMethodData == null) {
        console.warn("[JS] extractToken: paymentData.paymentMethodData is not available or null/undefined");
        return JsResult.error("Payment method data is not available in payment data");
    }
    console.log("[JS] extractToken: paymentData.paymentMethodData is available");

    if (typeof paymentData.paymentMethodData.tokenizationData === 'undefined' || paymentData.paymentMethodData.tokenizationData == null) {
        console.warn("[JS] extractToken: paymentData.paymentMethodData.tokenizationData is not available or null/undefined");
        return JsResult.error("Tokenization data is not available in payment method data");
    }
    console.log("[JS] extractToken: paymentData.paymentMethodData.tokenizationData is available");

    if (typeof paymentData.paymentMethodData.tokenizationData.token === 'undefined' || paymentData.paymentMethodData.tokenizationData.token == null) {
        console.warn("[JS] extractToken: paymentData.paymentMethodData.tokenizationData.token is not available or null/undefined");
        return JsResult.error("Payment token is not available in tokenization data");
    }

    const token = paymentData.paymentMethodData.tokenizationData.token;
    console.log("[JS] extractToken: payment token extracted successfully, length:", token?.length || 0);
    return JsResult.success(token);
}

/**
 * Check if Google Pay is ready to pay
 * @param {Object} paymentDataRequest - Payment data request object
 * @param {string} environment - Google Pay environment: 'TEST' or 'PRODUCTION'
 * @returns {Promise<JsResult>} Promise that resolves to JsResult containing ready to pay response or error
 */
export async function isReadyToPay(paymentDataRequest, environment) {
    console.log("[JS] isReadyToPay called with request:", paymentDataRequest, "environment:", environment);

    // Check if paymentDataRequest is null/undefined
    if (paymentDataRequest == null) {
        console.warn("[JS] isReadyToPay: paymentDataRequest is null/undefined");
        return JsResult.error("Payment data request is null or undefined");
    }

    // Validate environment parameter
    if (environment == null || (environment !== 'TEST' && environment !== 'PRODUCTION')) {
        console.warn("[JS] isReadyToPay: invalid environment, defaulting to TEST");
        environment = 'TEST';
    }

    // Step-by-step validation of Google Pay API chain (same as loadPaymentData)
    if (typeof google === 'undefined' || google == null) {
        console.warn("[JS] isReadyToPay: 'google' is not available or null/undefined");
        return JsResult.error("Google Pay API not loaded - 'google' object is not available");
    }
    console.log("[JS] isReadyToPay: 'google' object is available");

    if (typeof google.payments === 'undefined' || google.payments == null) {
        console.warn("[JS] isReadyToPay: 'google.payments' is not available or null/undefined");
        return JsResult.error("Google Pay API not loaded - 'google.payments' is not available");
    }
    console.log("[JS] isReadyToPay: 'google.payments' object is available");

    if (typeof google.payments.api === 'undefined' || google.payments.api == null) {
        console.warn("[JS] isReadyToPay: 'google.payments.api' is not available or null/undefined");
        return JsResult.error("Google Pay API not loaded - 'google.payments.api' is not available");
    }
    console.log("[JS] isReadyToPay: 'google.payments.api' object is available");

    if (typeof google.payments.api.PaymentsClient === 'undefined' || google.payments.api.PaymentsClient == null) {
        console.warn("[JS] isReadyToPay: 'google.payments.api.PaymentsClient' is not available or null/undefined");
        return JsResult.error("Google Pay API not loaded - 'google.payments.api.PaymentsClient' is not available");
    }
    console.log("[JS] isReadyToPay: 'google.payments.api.PaymentsClient' constructor is available");

    try {
        console.log("[JS] isReadyToPay: Creating PaymentsClient with environment:", environment);
        const paymentsClient = new google.payments.api.PaymentsClient({environment: environment});
        console.log("[JS] isReadyToPay: PaymentsClient instance created successfully");

        if (paymentsClient == null) {
            console.warn("[JS] isReadyToPay: PaymentsClient instance is null");
            return JsResult.error("Failed to create PaymentsClient instance");
        }

        if (typeof paymentsClient.isReadyToPay !== 'function') {
            console.warn("[JS] isReadyToPay: PaymentsClient.isReadyToPay method is not available");
            return JsResult.error("PaymentsClient.isReadyToPay method is not available");
        }

        // Validate request properties
        if (typeof paymentDataRequest.apiVersion === 'undefined') {
            console.warn("[JS] isReadyToPay: paymentDataRequest.apiVersion is undefined");
        }
        if (typeof paymentDataRequest.apiVersionMinor === 'undefined') {
            console.warn("[JS] isReadyToPay: paymentDataRequest.apiVersionMinor is undefined");
        }
        if (typeof paymentDataRequest.allowedPaymentMethods === 'undefined' || paymentDataRequest.allowedPaymentMethods == null) {
            console.warn("[JS] isReadyToPay: paymentDataRequest.allowedPaymentMethods is null/undefined");
        }

        const isReadyToPayRequest = {
            apiVersion: paymentDataRequest.apiVersion,
            apiVersionMinor: paymentDataRequest.apiVersionMinor,
            allowedPaymentMethods: paymentDataRequest.allowedPaymentMethods
        };
        console.log("[JS] isReadyToPay: isReadyToPayRequest created:", isReadyToPayRequest);
        console.log("[JS] isReadyToPay: calling PaymentsClient.isReadyToPay");

        const readyToPayPromise = paymentsClient.isReadyToPay(isReadyToPayRequest);
        console.log("[JS] isReadyToPay: PaymentsClient.isReadyToPay called successfully, awaiting promise");

        const readyToPayResponse = await readyToPayPromise;
        console.log("[JS] isReadyToPay: Promise resolved successfully", readyToPayResponse);
        const isReady = !!(readyToPayResponse && readyToPayResponse.result === true);
        return JsResult.success(isReady);

    } catch (error) {
        console.warn("[JS] isReadyToPay: Error creating PaymentsClient or calling isReadyToPay:", error);
        return JsResult.error(`Error creating PaymentsClient or calling isReadyToPay: ${error.message || error}`);
    }
}

/**
 * Google Pay object with all Google Pay-related functions
 * Available in global scope for external declarations
 * All methods now return JsResult objects for better Kotlin/WASM interop
 */
const GooglePayObj = {
    createPaymentRequest: function (merchantId, merchantName, acceptorId, totalPrice, gateway, currencyCode) {
        console.log("[JS] GooglePayObj.createPaymentRequest called");
        return createPaymentRequest(merchantId, merchantName, acceptorId, totalPrice, gateway, currencyCode);
    },

    loadPaymentData: async function (paymentDataRequest, environment) {
        console.log("[JS] GooglePayObj.loadPaymentData called");
        return await loadPaymentData(paymentDataRequest, environment);
    },

    extractToken: function (paymentData) {
        console.log("[JS] GooglePayObj.extractToken called");
        return extractToken(paymentData);
    },

    isReadyToPay: async function (paymentDataRequest, environment) {
        console.log("[JS] GooglePayObj.isReadyToPay called");
        return await isReadyToPay(paymentDataRequest, environment);
    }
};


// Make objects available globally for external declarations
globalThis.GooglePayObj = GooglePayObj;

// Also export objects as named exports
export {GooglePayObj};
